import React from "react";
import {useTrackingGroup} from "../context/TrackingContext";
import {EVENT_NAMES} from "../core/eventNames";
import type {PushOptions} from "../core/types";
import type {SingleLabelInput} from "../events/navigation";
import {toSingleLabelPayload, toMainNavigationPayload} from "../events/navigation";

/**
 * Domain-specific navigation tracking hook.
 *
 * - Keeps components free from tracking mechanics (no buffer/end timers needed)
 * - Starts an interaction for the respective navigation event so override rules apply
 * - Normalizes payloads via event-specific converters to ensure canonical schema
 */
export function useNavigationTracking() {
    const {startInteraction, push} = useTrackingGroup();

    const trackMetaNavigation = React.useCallback(
        (label?: SingleLabelInput, options?: PushOptions) => {
            startInteraction(EVENT_NAMES.META_NAVIGATION);
            void push(EVENT_NAMES.META_NAVIGATION, toSingleLabelPayload(label), options);
        },
        [startInteraction, push]
    );

    const trackBreadcrumb = React.useCallback(
        (label?: SingleLabelInput, options?: PushOptions) => {
            startInteraction(EVENT_NAMES.BREADCRUMB);
            void push(EVENT_NAMES.BREADCRUMB, toSingleLabelPayload(label), options);
        },
        [startInteraction, push]
    );

    const trackTopicNavigation = React.useCallback(
        (label?: SingleLabelInput, options?: PushOptions) => {
            startInteraction(EVENT_NAMES.TOPIC_NAVIGATION);
            void push(EVENT_NAMES.TOPIC_NAVIGATION, toSingleLabelPayload(label), options);
        },
        [startInteraction, push]
    );

    const trackMainNavigation = React.useCallback(
        (level: number, label?: string | null, options?: PushOptions & { isMobile?: boolean }) => {
            const {isMobile, ...pushOptions} = options ?? {};
            startInteraction(EVENT_NAMES.NAVIGATION);
            void push(
                EVENT_NAMES.NAVIGATION,
                toMainNavigationPayload(label, level, isMobile),
                pushOptions
            );
        },
        [startInteraction, push]
    );

    return {
        trackMetaNavigation,
        trackBreadcrumb,
        trackTopicNavigation,
        trackMainNavigation,
    } as const;
}